<script setup lang="ts">
import { ChevronDown, Menu, X } from 'lucide-vue-next'
import { services } from '~/data/services'

const route = useRoute()

const links = [
  { label: 'Work', href: '/work' },
  { label: 'CMS Migration', href: '/cms-migration' },
  { label: 'Support', href: '/ongoing-support' },
  { label: 'Inventorai', href: '/inventorai' },
  { label: 'Blog', href: '/blog' },
]

// On the home page the bar sits over the full-height hero; everywhere else it
// takes its own row at the top of the page.
const isHome = computed(() => route.path === '/')

// Past the first screenful the bar pins to the top and slides in, frosted, as
// the Inventorai site's header does.
const pinned = ref(false)
const onScroll = () => {
  pinned.value = window.scrollY > 160
}

const servicesOpen = ref(false)
const mobileOpen = ref(false)
const close = () => {
  servicesOpen.value = false
  mobileOpen.value = false
}

watch(() => route.fullPath, close)

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') close()
}

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('keydown', onKeydown)
})

const isActive = (href: string) => route.path === href || route.path.startsWith(`${href}/`)

// The blog tag panel's glass: the page colour at 60% over a blur, a thin edge.
const frosted = 'bg-[rgba(249,250,251,0.6)] backdrop-blur-md border border-slate-900/10 shadow-lg dark:bg-[rgba(15,15,26,0.6)] dark:border-white/10'
</script>

<template>
  <div :class="isHome ? 'h-0' : 'h-16'">
    <header
      class="inset-x-0 top-0 z-[200] h-16"
      :class="pinned ? 'fixed animate-slide-down border-b border-slate-900/10 dark:border-white/10' : 'absolute'"
    >
      <!-- The pinned bar's frost, on a layer of its own with nothing inside,
           as the Inventorai header does it. A blur on the header itself would
           make it the backdrop for everything in it, so the Services panel's
           own frost would only ever see the bar, never the page. -->
      <div
        v-if="pinned"
        class="pointer-events-none absolute inset-0 -z-10 bg-[rgba(249,250,251,0.6)] backdrop-blur-md dark:bg-[rgba(15,15,26,0.6)]"
        aria-hidden="true"
      ></div>
      <div class="mx-auto flex h-full max-w-7xl items-center justify-between gap-6 px-6 md:px-12">
        <NuxtLink to="/" class="group shrink-0" aria-label="AugmentBLU home">
          <span class="text-xl font-bold tracking-tight text-slate-700 dark:text-white">
            AUGMENT<span class="text-blu-500 transition-colors group-hover:text-blu-600 dark:group-hover:text-blu-400">BLU</span>
          </span>
        </NuxtLink>

        <!-- Desktop -->
        <nav class="hidden items-center gap-8 lg:flex" aria-label="Main">
          <div class="relative" @mouseenter="servicesOpen = true" @mouseleave="servicesOpen = false">
            <button
              type="button"
              class="flex items-center gap-1 text-sm font-medium transition-colors"
              :class="isActive('/services') ? 'text-blu-600 dark:text-blu-400' : 'text-slate-700/80 hover:text-slate-900 dark:text-white/65 dark:hover:text-white'"
              :aria-expanded="servicesOpen"
              aria-haspopup="true"
              @click="servicesOpen = !servicesOpen"
            >
              Services
              <ChevronDown class="h-3.5 w-3.5 transition-transform duration-200" :class="servicesOpen && 'rotate-180'" />
            </button>

            <Transition
              enter-active-class="transition-transform duration-200 ease-out"
              leave-active-class="transition-transform duration-100 ease-in"
              enter-from-class="-translate-y-1"
              leave-to-class="-translate-y-1"
            >
              <!-- Only the drop is animated, never the opacity: Chrome holds
                   back a backdrop blur while its element, or anything round it,
                   is part transparent, so a fade would leave the glass to
                   arrive after the panel. -->
              <!-- pt-4 bridges the gap so the pointer can travel into the panel. -->
              <div v-if="servicesOpen" class="absolute left-1/2 top-full -translate-x-1/2 pt-4">
                <div class="w-[34rem] rounded-2xl p-3" :class="frosted">
                  <ul class="grid grid-cols-2 gap-1">
                    <li v-for="service in services" :key="service.slug">
                      <NuxtLink
                        :to="`/services/${service.slug}`"
                        class="block rounded-xl px-3 py-2.5 text-sm font-medium transition-colors"
                        :class="route.path === `/services/${service.slug}`
                          ? 'text-blu-600 dark:text-blu-400 bg-blu-500/10'
                          : 'text-slate-700 hover:bg-slate-900/5 dark:text-white/80 dark:hover:bg-white/5 dark:hover:text-white'"
                      >
                        {{ service.title }}
                      </NuxtLink>
                    </li>
                  </ul>
                  <div class="mt-2 border-t border-slate-900/10 px-3 pt-3 pb-1 dark:border-white/10">
                    <NuxtLink to="/services" class="text-sm font-medium text-blu-600 underline decoration-dotted underline-offset-4 transition-colors hover:decoration-solid dark:text-blu-400">
                      All services
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <NuxtLink
            v-for="link in links"
            :key="link.href"
            :to="link.href"
            class="text-sm font-medium transition-colors"
            :class="isActive(link.href) ? 'text-blu-600 dark:text-blu-400' : 'text-slate-700/80 hover:text-slate-900 dark:text-white/65 dark:hover:text-white'"
          >
            {{ link.label }}
          </NuxtLink>
        </nav>

        <div class="flex items-center gap-2">
          <ThemeToggle />
          <NuxtLink
            to="/contact"
            class="hidden rounded-full bg-blu-500 px-5 py-2 text-sm font-semibold text-dark-500 transition-colors hover:bg-blu-400 sm:inline-flex"
          >
            Get in touch
          </NuxtLink>
          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-full text-slate-700 dark:text-white lg:hidden"
            :aria-expanded="mobileOpen"
            aria-label="Toggle menu"
            @click="mobileOpen = !mobileOpen"
          >
            <X v-if="mobileOpen" class="h-5 w-5" />
            <Menu v-else class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- Mobile -->
      <Transition
        enter-active-class="transition-transform duration-200 ease-out"
        leave-active-class="transition-transform duration-100 ease-in"
        enter-from-class="-translate-y-1"
        leave-to-class="-translate-y-1"
      >
        <nav
          v-if="mobileOpen"
          class="absolute inset-x-4 top-full mt-2 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-2xl p-2 lg:hidden"
          :class="frosted"
          aria-label="Main"
        >
          <NuxtLink
            v-for="link in [{ label: 'Services', href: '/services' }, ...links, { label: 'FAQ', href: '/faq' }, { label: 'Contact', href: '/contact' }]"
            :key="link.href"
            :to="link.href"
            class="block rounded-xl px-4 py-3 text-base font-medium transition-colors"
            :class="isActive(link.href)
              ? 'text-blu-600 dark:text-blu-400 bg-blu-500/10'
              : 'text-slate-700 hover:bg-slate-900/5 dark:text-white/80 dark:hover:bg-white/5'"
          >
            {{ link.label }}
          </NuxtLink>
        </nav>
      </Transition>
    </header>
  </div>
</template>
